/**
 * 认证API客户端 - L2 API封装层
 * 封装所有认证相关的API调用，提供类型安全的接口
 */
import { AUTH_ENDPOINTS } from './endpoints.js';
/**
 * 认证API客户端
 *
 * 设计原则：
 * 1. 薄封装：只负责API调用，不包含业务逻辑
 * 2. 类型安全：所有接口都有完整的TypeScript类型
 * 3. 一致性：与后端AuthController接口保持一致
 * 4. 可测试：易于单元测试和模拟
 */
export class AuthClient {
    constructor(httpClient, supabase) {
        this.httpClient = httpClient;
        this.supabase = supabase;
    }
    // =================
    // 核心认证方法
    // =================
    /**
     * 用户注册
     * 对应：POST /api/auth/register
     */
    async register(request) {
        const response = await this.httpClient.post(AUTH_ENDPOINTS.REGISTER, request);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Registration failed');
        }
        return response.data;
    }
    /**
     * 用户登录
     * 对应：POST /api/auth/login
     * 支持Cookie和Token双模式
     */
    async login(request) {
        const response = await this.httpClient.post(AUTH_ENDPOINTS.LOGIN, request);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Login failed');
        }
        // 关键：将从后端获取的会话同步到Supabase客户端
        if (response.data.session) {
            await this.supabase.auth.setSession({
                access_token: response.data.session.accessToken,
                refresh_token: response.data.session.refreshToken,
            });
        }
        return response.data;
    }
    /**
     * 用户登出
     * 对应：POST /api/auth/logout
     */
    async logout() {
        // 使用Promise.allSettled确保两个操作都会被尝试
        const [apiResult, clientResult] = await Promise.allSettled([
            this.httpClient.post(AUTH_ENDPOINTS.LOGOUT),
            this.supabase.auth.signOut(),
        ]);
        // 改进错误处理：区分不同类型的错误
        if (apiResult.status === 'rejected') {
            const error = apiResult.reason;
            // 对于某些预期的错误（如会话已过期），只记录警告而不是错误
            if (error?.message?.includes('Auth session missing') ||
                error?.message?.includes('session missing') ||
                error?.status === 401) {
                console.warn('API logout failed due to expired/missing session, but this is expected:', error?.message);
            }
            else {
                console.error('API logout failed:', apiResult.reason);
            }
        }
        if (clientResult.status === 'rejected') {
            console.error('Supabase client signOut failed:', clientResult.reason);
        }
    }
    /**
     * 刷新访问令牌
     * 对应：POST /api/auth/refresh-token
     * 支持Cookie和Token双模式
     */
    async refreshToken(request) {
        const response = await this.httpClient.post(AUTH_ENDPOINTS.REFRESH_TOKEN, request);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Token refresh failed');
        }
        return response.data;
    }
    // =================
    // 用户信息方法
    // =================
    /**
     * 获取当前用户信息
     * 对应：GET /users/me
     */
    async getCurrentUser() {
        const response = await this.httpClient.get(AUTH_ENDPOINTS.CURRENT_USER);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to get current user');
        }
        return response.data;
    }
    // =================
    // 密码管理方法
    // =================
    /**
     * 修改密码
     * 对应：PUT /api/auth/change-password
     */
    async changePassword(request) {
        const response = await this.httpClient.put(AUTH_ENDPOINTS.CHANGE_PASSWORD, request);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Password change failed');
        }
        return response.data;
    }
    // =================
    // 邮箱验证方法
    // =================
    /**
     * 重发验证邮件
     * 对应：POST /api/auth/resend-verification
     */
    async resendVerificationEmail(request) {
        const response = await this.httpClient.post(AUTH_ENDPOINTS.RESEND_VERIFICATION, request);
        if (!response.success) {
            throw new Error(response.error || 'Failed to resend verification email');
        }
    }
    /**
     * 验证邮箱
     * 对应：POST /api/auth/verify-email
     */
    async verifyEmail(request) {
        const response = await this.httpClient.post(AUTH_ENDPOINTS.VERIFY_EMAIL, request);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Email verification failed');
        }
        return response.data;
    }
}
//# sourceMappingURL=client.js.map